import { ref, nextTick } from 'vue'
import { getLoginToken } from '../api/axios.js'
import {
  assertLoginEnvironment,
  expireAuthSession,
  isUnauthorizedResponse
} from '../api/auth.js'
import { calculateInverseKinematics, calculateRelativeAngle } from '../utils/kinematics.js'
import {
  isNestedTrajectory,
  findPersonConfigByPersonName,
  findPersonIdByPersonName,
  findPersonConfigByPersonId,
  getOriginPosition
} from '../utils/trajectoryConfig.js'
import { emotionOptions } from '../constants/emotions.js'

// 操作记录的保存（到 Manager）与还原
// 外部依赖（均为 Ref，除函数外）：
//   servos / trajectoryConfigs / currentTrajectoryId / showDualServoArm
//   trajectoryX / trajectoryY / trajectoryListData
//   selectedEnv / deviceId / servoVersion
//   operationName / totalDuration / selectedEmotion / emojiVersion / refreshTrigger
//   isRestoring    - 还原中标记 Ref（由 App 创建，供联动 watch 共享，避免还原过程触发联动）
//   buildSendData  - 组装下发数据的函数 (saveToDevice, allowEmpty) => mcpData
//   showSnackbar   - 显示提示信息的回调 (msg: string) => void
export function useOperationRecord(deps) {
  const {
    servos, trajectoryConfigs, currentTrajectoryId, showDualServoArm,
    trajectoryX, trajectoryY, trajectoryListData,
    selectedEnv, deviceId, servoVersion,
    operationName, totalDuration, selectedEmotion, emojiVersion, refreshTrigger,
    isRestoring, buildSendData, showSnackbar
  } = deps

  const saveConfirmDialog = ref(false) // 保存确认弹窗
  const dataId = ref(null)             // 当前记录ID（保存后回填，用于更新）

  // 保存确认
  const confirmSave = async () => {
    saveConfirmDialog.value = false
    await goRecord()
  }

  // 保存记录到 Manager
  const goRecord = async () => {
    const loginToken = getLoginToken()
    if (!loginToken) {
      expireAuthSession()
      return
    }

    try {
      assertLoginEnvironment(selectedEnv.value.manager_url)
    } catch {
      return
    }

    // 保存前自动补全c字段
    const normalServoData = servos.value.map(s => ({...s, c: s.m.length}))

    // 收集轨迹舵机数据和轨迹坐标数据
    const trajectoryServoData = []
    const trajectoryData = []
    trajectoryConfigs.value.forEach(trajectory => {
      if (trajectory.servos && trajectory.servos.length > 0) {
        // 收集舵机数据
        trajectory.servos.forEach(servo => {
          trajectoryServoData.push({...servo, c: servo.m.length})
        })

        // 收集轨迹坐标数据
        if (trajectory.trajectoryPoints && trajectory.trajectoryPoints.length > 0) {
          const trajectoryCoords = trajectory.trajectoryPoints.map((point, idx) => {
            // 除了最后一个点，每个点都有对应的角度片段
            // 步骤数来自第一个舵机的motion片段
            const step = idx < trajectory.trajectoryPoints.length - 1 && trajectory.servos[0]?.m[idx]
              ? trajectory.servos[0].m[idx].steps
              : 0

            return {
              x: point.x,
              y: point.y,
              step: step
            }
          })

          trajectoryData.push({
            name: trajectory.name, // 完整名称，格式为 "轨迹名称-人物名称" 或 "轨迹名称"
            trajectoryName: trajectory.trajectoryName || trajectory.name.split('-')[0], // 轨迹名称
            personName: trajectory.personName || (trajectory.name.includes('-') ? trajectory.name.split('-').slice(1).join('-') : ''), // 人物名称（用于显示）
            personId: trajectory.personId || '', // 数字键（用于还原，优先级高于 personName）
            servos: trajectory.servos.map(s => s.s), // 舵机ID列表
            coordinates: trajectoryCoords,
            zeroPositionAngles: trajectory.zeroPositionAngles || { servoA: 0, servoB: 0 } // 零位角度
          })
        }
      }
    })

    // 合并所有舵机数据
    const data = [...normalServoData, ...trajectoryServoData]

    const emotion = emotionOptions.find(e => e.value === selectedEmotion.value)
    // 允许无舵机时保存记录，motion 以空数组提交给 Manager。
    const mcpData = buildSendData(true, true)
    if (!mcpData) return
    mcpData.params.arguments.motion = JSON.parse(mcpData.params.arguments.motion)
    console.info('motion', mcpData.params.arguments.motion)
    try {
      const response = await fetch(`${selectedEnv.value.manager_url}/xiaozhi/robot/operation/save`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': loginToken
            },
            body: JSON.stringify({
              "operationName": operationName.value,
              "id": dataId.value,
              "emoji": emotion ? emotion.value : null,
              "version": emojiVersion.value,
              "deviceId": deviceId.value,
              "servoVersion": servoVersion.value,
              "params": JSON.stringify({
                "operationName": operationName.value,
                "totalTime": totalDuration.value,
                "servoParamList": data,
                "trajectory": trajectoryData.length > 0 ? trajectoryData : undefined
              }),
              "mcpData": JSON.stringify({mcpData}),
            })
          }
      )
      const result = await response.json().catch(() => null)
      if (isUnauthorizedResponse(response.status, result)) {
        expireAuthSession()
        return
      }
      if (!response.ok || result?.code !== 0) {
        throw new Error(result?.msg || `请求失败 (${response.status})`)
      }
      dataId.value = result.data.id
      emojiVersion.value = result.data.version
      servoVersion.value = result.data.servoVersion
      showSnackbar('保存成功')

      // 保存成功后刷新操作记录
      refreshTrigger.value++
    } catch (err) {
      console.error('数据获取失败:', err)
    }
  }

  // 还原配置
  const handleRestoreConfig = async (itemData) => {
    const params = itemData.params
    if (params && params.servoParamList) {
      isRestoring.value = true

      // 判断是否有轨迹数据
      const hasTrajectory = params.trajectory && params.trajectory.length > 0

      if (hasTrajectory) {
        // 有轨迹数据，需要分别处理普通舵机和轨迹舵机
        const normalServos = []
        const trajectoryServos = []

        params.servoParamList.forEach(servo => {
          // 检查这个舵机是否属于某个轨迹
          const belongsToTrajectory = params.trajectory.some(traj =>
            traj.servos && traj.servos.includes(servo.s)
          )
          if (belongsToTrajectory) {
            trajectoryServos.push(servo)
          } else {
            normalServos.push(servo)
          }
        })

        servos.value = normalServos

        // 还原轨迹数据
        trajectoryConfigs.value = []
        params.trajectory.forEach((traj, idx) => {
          const trajectoryServosForThisTraj = params.servoParamList.filter(servo =>
            traj.servos && traj.servos.includes(servo.s)
          )

          if (trajectoryServosForThisTraj.length > 0 && traj.coordinates && traj.coordinates.length > 0) {
            const trajectoryPoints = traj.coordinates.map(coord => ({
              x: coord.x,
              y: coord.y
            }))

            // 解析轨迹名称和人物标识
            const trajectoryName = traj.trajectoryName || traj.name.split('-')[0]
            const personId = traj.personId || null // 优先使用数字键（personId）
            const personName = traj.personName || (traj.name.includes('-') ? traj.name.split('-').slice(1).join('-') : '')

            // 从 trajectoryList.json 中查找对应的配置
            let trajectoryConfigFromList = null
            let actualPersonName = personName
            if (trajectoryListData.value[trajectoryName]) {
              const trajectoryGroup = trajectoryListData.value[trajectoryName]
              // 如果是嵌套结构
              if (isNestedTrajectory(trajectoryGroup)) {
                // 优先使用 personId（数字键）查找配置
                if (personId && trajectoryGroup[personId]) {
                  trajectoryConfigFromList = findPersonConfigByPersonId(trajectoryGroup, personId)
                  // 从配置中获取最新的 personName（因为配置可能会变）
                  actualPersonName = trajectoryConfigFromList?.personName || personName
                } else if (personName) {
                  // 如果没有 personId，回退到使用 personName 查找（向后兼容）
                  trajectoryConfigFromList = findPersonConfigByPersonName(trajectoryGroup, personName)
                  // 同时查找对应的 personId
                  const foundPersonId = findPersonIdByPersonName(trajectoryGroup, personName)
                  if (foundPersonId && trajectoryConfigFromList) {
                    actualPersonName = trajectoryConfigFromList.personName || personName
                  }
                }
              } else if (!isNestedTrajectory(trajectoryGroup)) {
                // 旧格式，直接使用
                trajectoryConfigFromList = trajectoryGroup
              }
            }

            const fullName = actualPersonName ? `${trajectoryName}-${actualPersonName}` : traj.name

            // 创建轨迹配置对象
            const newTrajectory = {
              id: Date.now() + idx,
              name: fullName,
              trajectoryName: trajectoryName,
              personName: actualPersonName || '',
              personId: personId || findPersonIdByPersonName(trajectoryListData.value[trajectoryName], actualPersonName) || '',
              config: trajectoryConfigFromList ? {
                name: fullName,
                trajectoryName: trajectoryName,
                personName: actualPersonName || '',
                personId: personId || findPersonIdByPersonName(trajectoryListData.value[trajectoryName], actualPersonName) || '',
                ...trajectoryConfigFromList
              } : {
                name: fullName,
                trajectoryName: trajectoryName,
                personName: actualPersonName || '',
                personId: personId || ''
              },
              servos: trajectoryServosForThisTraj.map(servo => ({
                ...servo,
                s: servo.s,
                name: servo.name || `舵机${servo.s}`,
                range: servo.range || [-200, 200],
                l: servo.l || 1,
                m: servo.m || []
              })),
              armConfig: { servoA: 0, servoB: 0 },
              trajectoryX: trajectoryPoints[0]?.x || 0,
              trajectoryY: trajectoryPoints[0]?.y || 0,
              trajectoryPoints: trajectoryPoints,
              // 还原零位信息（优先使用保存的数据，如果没有则使用默认值）
              zeroPositionAngles: traj.zeroPositionAngles || { servoA: 0, servoB: 0 }
            }

            // 如果从 trajectoryList.json 中找到了配置，计算初始位置的舵机角度
            if (trajectoryConfigFromList && trajectoryConfigFromList.armLengths && trajectoryConfigFromList.servoDistance) {
              const initialX = trajectoryPoints[0]?.x || trajectoryConfigFromList.x || 0
              const initialY = trajectoryPoints[0]?.y || trajectoryConfigFromList.y || 0

              const angles = calculateInverseKinematics(
                initialX,
                initialY,
                trajectoryConfigFromList.armLengths,
                trajectoryConfigFromList.servoDistance
              )

              if (angles) {
                // 如果保存的数据中没有零位角度，使用 trajectoryList.json 中的零位计算
                if (!traj.zeroPositionAngles) {
                  const zeroPosition = getOriginPosition(trajectoryConfigFromList, 'origin')
                  const zeroAngles = calculateInverseKinematics(
                    zeroPosition.x,
                    zeroPosition.y,
                    trajectoryConfigFromList.armLengths,
                    trajectoryConfigFromList.servoDistance
                  )

                  if (zeroAngles) {
                    newTrajectory.zeroPositionAngles.servoA = zeroAngles.servoA
                    newTrajectory.zeroPositionAngles.servoB = zeroAngles.servoB
                  }
                }

                // armConfig 初始为相对于零位的偏移
                newTrajectory.armConfig.servoA = calculateRelativeAngle(angles.servoA, newTrajectory.zeroPositionAngles.servoA)
                newTrajectory.armConfig.servoB = calculateRelativeAngle(angles.servoB, newTrajectory.zeroPositionAngles.servoB)
              }
            }

            trajectoryConfigs.value.push(newTrajectory)
          }
        })

        // 设置当前轨迹
        if (trajectoryConfigs.value.length > 0) {
          currentTrajectoryId.value = trajectoryConfigs.value[0].id
          showDualServoArm.value = true
          // 更新 trajectoryX 和 trajectoryY 以显示当前坐标
          const currentTrajectory = trajectoryConfigs.value[0]
          trajectoryX.value = currentTrajectory.trajectoryX
          trajectoryY.value = currentTrajectory.trajectoryY
        }
      } else {
        // 没有轨迹数据，正常还原
        servos.value = params.servoParamList
        trajectoryConfigs.value = []
        currentTrajectoryId.value = null
      }

      dataId.value = itemData.id
      const emotion = emotionOptions.find(e => e.value === itemData.emoji)
      selectedEmotion.value = emotion ? emotion.value : null
      emojiVersion.value = itemData.version
      servoVersion.value = itemData.servoVersion
      // 更新总时长
      if (params.totalTime) {
        totalDuration.value = params.totalTime
      }
      // 更新操作名称
      if (params.operationName) {
        operationName.value = params.operationName
      }
      // 显示还原成功提示
      showSnackbar(hasTrajectory ? '轨迹配置还原成功' : '配置还原成功')

      await nextTick()
      isRestoring.value = false
    } else {
      showSnackbar('配置参数无效')
    }
  }

  return {
    saveConfirmDialog,
    dataId,
    confirmSave,
    goRecord,
    handleRestoreConfig
  }
}
